import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { CreatorResponseDto, CreatorService } from '@/app/services/creator/creator.service';
import { ImageService } from '@/app/services/image/image.service';
import {
  creatorInsightsNavItems,
  CreatorInsightsNavItem,
  CreatorInsightsSection,
} from '../../insights-nav';

@Component({
  selector: 'ins-creator',
  imports: [RouterOutlet],
  templateUrl: './creator.html',
  styleUrl: './creator.css',
  host: { class: 'flex flex-col h-full min-h-0 flex-1 w-full min-w-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Creator implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private creatorService = inject(CreatorService);
  readonly imageService = inject(ImageService);

  readonly creatorId = signal<string | undefined>(undefined);
  readonly creator = signal<CreatorResponseDto | undefined>(undefined);
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects.split('?')[0] ?? ''),
      startWith(this.router.url.split('?')[0] ?? ''),
    ),
    { initialValue: this.router.url.split('?')[0] ?? '' },
  );

  readonly navItems = computed(() => {
    const c = this.creator();
    const shoutHandle = c?.['shoutHandle'] as string | undefined;
    return creatorInsightsNavItems.filter(
      (item) => !item.requiresShoutHandle || !!shoutHandle,
    );
  });

  readonly activeSection = computed((): CreatorInsightsSection | null => {
    const url = this.currentUrl();
    const match = url.match(/\/insights\/creator\/[^/]+\/([^/?]+)/);
    return (match?.[1] as CreatorInsightsSection) ?? null;
  });

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = params.get('creatorId') ?? undefined;
      this.creatorId.set(id);
      if (id) {
        this.loadCreator(id);
      }
    });
  }

  goToSection(item: CreatorInsightsNavItem): void {
    const id = this.creatorId();
    if (!id) {
      return;
    }
    void this.router.navigate(['/insights/creator', id, item.section]);
  }

  getNavClass(item: CreatorInsightsNavItem): string {
    if (this.activeSection() === item.section) {
      return 'border-primary text-accent';
    }
    return 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-300';
  }

  private loadCreator(creatorId: string): void {
    this.loadingState.set('loading');
    this.creatorService
      .getCreatorById(creatorId)
      .pipe(
        catchError(() => {
          this.loadingState.set('error');
          return of(undefined);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((creator) => {
        if (creator) {
          this.creator.set(creator);
          this.loadingState.set('loaded');
        }
      });
  }
}
